import { isEmpty } from "@bodil/core/assert";
import { attribute, attributeGetter, Component, type Emits } from "@bodil/dom/component";
import type { Role } from "aria-attribute-types";
import { html, nothing } from "lit";
import { customElement } from "lit/decorators.js";
import { live } from "lit/directives/live.js";
import { createRef, ref } from "lit/directives/ref.js";

import styles from "./radio.css?inline";

declare global {
    interface HTMLElementTagNameMap {
        "btk-radio": BtkRadio;
    }
}

@customElement("btk-radio")
export class BtkRadio extends Component {
    emits!: Emits<"change">;

    @attribute({ reflect: false }) accessor value = "";
    @attribute({ type: Boolean }) accessor checked = false;
    @attribute({ type: Boolean }) accessor disabled = false;
    @attribute accessor name = "";

    @attributeGetter get role(): Role {
        return "radio";
    }

    @attributeGetter({ reactive: true }) get ariaChecked(): "true" | "false" {
        return this.checked ? "true" : "false";
    }

    private readonly input = createRef<HTMLInputElement>();

    focus(options: FocusOptions) {
        this.input.value?.focus(options);
    }

    blur() {
        this.input.value?.blur();
    }

    click() {
        this.input.value?.click();
    }

    private handleChange() {
        this.checked = this.input.value?.checked === true;
        this.emit("change");
    }

    render() {
        return html` <label part="label">
            <input
                ${ref(this.input)}
                type="radio"
                name=${isEmpty(this.name) ? nothing : this.name}
                .value=${live(this.value)}
                .checked=${live(this.checked)}
                ?disabled=${this.disabled}
                @change=${this.handleChange}
            />
            <slot></slot>
        </label>`;
    }

    static styles = styles;
}
